import GithubSlugger from 'github-slugger';

export interface HeadingInfo {
  /** 1-6, the number of leading `#` characters. */
  depth: number;
  /** Heading text with the leading `#`s and any trailing `#`s stripped. */
  text: string;
  /** github-slugger slug — must exactly match GitHub's own anchor algorithm
   * so HELP_MAP entries resolve. This is the ONLY slug-computation path in
   * the feature: markdown.tsx renders heading ids from these precomputed
   * values (matched by source line), never from a second slugging pass. */
  slug: string;
  /** 0-based index into `lines` where this heading's line lives. */
  line: number;
}

/** A markdown document parsed once: its lines and its headings. Precomputed
 * at module scope for the three static guides (content.ts's PARSED_DOCS) so
 * no component ever re-parses a document at render time. */
export interface ParsedDoc {
  lines: string[];
  headings: HeadingInfo[];
}

/** A slice of a parsed document: the section's raw markdown plus the
 * headings inside it. Heading `slug`s are the DOC-level slugs (so a
 * duplicate heading deep inside the doc keeps its `-1`/`-2` suffix even
 * when its section is rendered standalone); `line`s are re-based to be
 * relative to `markdown`, because that is the string the renderer will
 * hand to remark (whose position info is relative to its own input). */
export interface DocSection {
  markdown: string;
  headings: HeadingInfo[];
}

// ATX headings, CommonMark-style: up to 3 leading spaces (4+ is an indented
// code block), 1-6 `#`s, at least one space/tab before the text, optional
// closing `#`s. The guides never use setext (`===`/`---` underline) headings.
const ATX_HEADING = /^ {0,3}(#{1,6})[ \t]+(.*?)[ \t]*#*[ \t]*$/;

// Fenced code blocks, CommonMark-style: an opener is up to 3 leading spaces
// then 3+ backticks or 3+ tildes plus an optional info string (a backtick
// fence's info string may not itself contain a backtick — that's inline code,
// not a fence). The block closes only at a fence of the SAME character, at
// least as LONG as the opener, with nothing but whitespace after it — so a
// ``` line inside a ~~~ block (or a ``` inside a ````` block) does not
// terminate it. Fences indented 4+ spaces (e.g. inside a list item) are not
// recognised — at top level that's an indented code block, whose lines can't
// match ATX_HEADING's 0-3 space limit anyway.
const FENCE_OPEN = /^ {0,3}(`{3,}|~{3,})(.*)$/;

interface FenceState {
  char: '`' | '~';
  length: number;
}

function tryOpenFence(line: string): FenceState | null {
  const match = FENCE_OPEN.exec(line);
  if (!match) return null;
  const marker = match[1] ?? '';
  const info = match[2] ?? '';
  const char = marker.startsWith('`') ? '`' : '~';
  if (char === '`' && info.includes('`')) return null;
  return { char, length: marker.length };
}

function closesFence(line: string, fence: FenceState): boolean {
  const match = /^ {0,3}(`{3,}|~{3,})[ \t]*$/.exec(line);
  if (!match) return false;
  const marker = match[1] ?? '';
  return marker.startsWith(fence.char) && marker.length >= fence.length;
}

function parseHeadingLine(line: string): { depth: number; text: string } | null {
  const match = ATX_HEADING.exec(line);
  if (!match) return null;
  const hashes = match[1] ?? '';
  const text = (match[2] ?? '').trim();
  if (!hashes || !text) return null;
  return { depth: hashes.length, text };
}

/** Parses a markdown string once: splits lines and extracts every ATX
 * heading, skipping fenced code blocks, slugging each heading with a single
 * github-slugger instance for the whole document — so duplicate headings get
 * GitHub's real `-1`, `-2`, ... de-duplication. Pure: same input, same
 * output, no shared state. Intended to run once per document at module
 * scope, not per render. */
export function parseDoc(markdown: string): ParsedDoc {
  const slugger = new GithubSlugger();
  // Line-ending normalization, once, at the single parse boundary every caller
  // funnels through (getHeadings, extractSection, content.ts's PARSED_DOCS).
  // Every regex below anchors at `$`, and in JavaScript `.`/`$` treat `\r` as
  // an ordinary character — so a CRLF-saved guide would otherwise match ZERO
  // headings and silently drop every in-app help anchor (exactly what happened
  // 2026-07-10 when a Windows edit flipped docs/ADMIN_GUIDE.md to CRLF; the
  // root .gitattributes now pins *.md to LF, and this is the belt to that
  // suspender). ParsedDoc.lines — and therefore DocSection.markdown and every
  // HeadingInfo.line — are the NORMALIZED lines, which stays consistent with
  // markdown.tsx's line→id lookup even when the renderer is handed a raw CRLF
  // string: remark counts a `\r\n` (or lone `\r`) line break as one line, the
  // same as the `\n` it became here, so positions can't diverge.
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const headings: HeadingInfo[] = [];
  let fence: FenceState | null = null;

  lines.forEach((line, index) => {
    if (fence) {
      if (closesFence(line, fence)) fence = null;
      return;
    }
    const opened = tryOpenFence(line);
    if (opened) {
      fence = opened;
      return;
    }

    const parsed = parseHeadingLine(line);
    if (!parsed) return;

    headings.push({
      depth: parsed.depth,
      text: parsed.text,
      slug: slugger.slug(parsed.text),
      line: index,
    });
  });

  return { lines, headings };
}

/** Convenience wrapper over parseDoc for callers/tests that only want the
 * headings of an ad-hoc string. */
export function getHeadings(markdown: string): HeadingInfo[] {
  return parseDoc(markdown).headings;
}

/** Extracts the section starting at the heading whose doc-level slug equals
 * `slug`, up to (but not including) the next heading of the same or
 * shallower depth — or the end of the document. Returns the section's raw
 * markdown plus its headings (doc-level slugs, section-relative lines — see
 * DocSection). Returns null if no heading in the doc slugs to `slug`. */
export function extractParsedSection(doc: ParsedDoc, slug: string): DocSection | null {
  const index = doc.headings.findIndex((h) => h.slug === slug);
  if (index === -1) return null;

  const heading = doc.headings[index];
  if (!heading) return null; // satisfies noUncheckedIndexedAccess (index is a hit from findIndex, so this can't fire)

  const next = doc.headings.slice(index + 1).find((h) => h.depth <= heading.depth);
  const endLine = next ? next.line : doc.lines.length;

  const headings = doc.headings
    .filter((h) => h.line >= heading.line && h.line < endLine)
    .map((h) => ({ ...h, line: h.line - heading.line }));

  return {
    markdown: doc.lines.slice(heading.line, endLine).join('\n').trimEnd(),
    headings,
  };
}

/** String-in, string-out variant of extractParsedSection for ad-hoc
 * markdown (unit tests, mainly) — production code goes through
 * extractParsedSection with content.ts's module-scope PARSED_DOCS instead,
 * so documents are parsed exactly once, not per call. */
export function extractSection(markdown: string, slug: string): string | null {
  return extractParsedSection(parseDoc(markdown), slug)?.markdown ?? null;
}
